import React from "react";
import { useState, useEffect, useContext, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Button,
  Collapse,
  Modal,
  ModalBody,
} from "reactstrap";
import Datetime from "react-datetime";
import moment from "moment";
import FalconCardHeader from "../common/FalconCardHeader";
import WizardError from "../auth/register/WizardError";
import ButtonIcon from "../common/ButtonIcon";
import classNames from "classnames";
import NumberFormat from "react-number-format";
import { faSearch, faUndo, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PreloadState } from "../UI/preloading/PreloadState";
import { fetchCondominiumBalance } from "../../helpers/api/condominium";
import {
  createIncome,
  fetchCondominiumFunds,
} from "../../helpers/api/finance";
import { fetchAccountPlans } from "../../helpers/api/accountPlans";
import { fetchAccountingAreas, createAccountingArea } from "../../helpers/api/accountingAreas";
import { fetchCondominiumBanks } from "../../helpers/api/banks";
import { findAllCurrencies } from "../../helpers/api/currency";
import { useSelector } from "react-redux";
import { useCallback } from "react";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import { useHistory, useParams } from "react-router-dom";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { Toast } from "../UI/toast/Toast";
import useSWRFetchPagination from "../../helpers/hooks/useSWRFetchPagination";
import useExchangeRateForDate from "../../helpers/hooks/useExchangeRateForDate";
import FullPagination from "../common/pagination/FullPagination";
import Loader from "../common/Loader";
import SelectBankListItem from "../common/SelectBankListItem";
import FileUploadArea from "../common/FileUploadArea";
import InputWithLeftSelect from "../common/CurrencyAmountInput";
import {
  convertToUsd,
  formatAmountRounded,
  getCurrencyName,
} from "../../helpers/currencyConversion";
import { CurrencyContext } from "../../context/Context";
import { getFinanceModuleBase } from "../../helpers/utils";
import { fetchAllPages } from "../../helpers/api/pagination";
const CreditPaymentForm = ({
  provider,
  handleSubmitData,
  setRedirect,
  setRedirectUrl,
  handleClose,
}) => {
  const history = useHistory();
  const { id } = useParams();
  const moduleBase = getFinanceModuleBase(history.location.pathname);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    setError,
    reset,
    errors,
    control,
    formState: { isSubmitting },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    shouldFocusError: true,
    shouldUnregister: false,
    defaultValues: {
      income_date: moment().format("YYYY-MM-DD HH:mm:ss"),
      montoOriginal: "",
      monedaOriginal: "",
    },
  });

  const { currency: curr, baseCurrency } = useContext(CurrencyContext);
  const activeExchangeRate = Number(curr?.value ?? 0);
  const [search, setSearch] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [collapseNofrecuente, collapseNoFrecuenteOpen] = useState(false);
  const [openFrecuente, setOpenFrecuente] = useState(false);
  const [dataCredit, setDataCredit] = useState();
  const [prefix, setPrefix] = useState();
  const [collapseCredito, collapseCreditoOpen] = useState(false);
  const [openCredito, setOpencredito] = useState(false);
  const [file, setFile] = useState(null);
  const {
    condominium: { selectedCondominium },
    auth: {
      user: { role },
    },
  } = useSelector((state) => state);

  const [params, setParams] = useState({
    condominiumId: selectedCondominium,
    search: null,
  });

  // Moneda + fecha del ingreso: mismo mecanismo que en los formularios de
  // gastos (currencyCatalog/preferredDefaultCurrency/useExchangeRateForDate),
  // para poder registrar un ingreso en Bolívares y convertirlo a la moneda
  // base usando la tasa vigente para la fecha elegida.
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [hasLoadedCurrencies, setHasLoadedCurrencies] = useState(false);
  const [hasInitializedCurrencyDefault, setHasInitializedCurrencyDefault] = useState(false);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadCurrencies = async () => {
      if (!selectedCondominium) return;

      setHasLoadedCurrencies(false);

      try {
        const response = await fetchAllPages((page, pageSize) => findAllCurrencies(page, pageSize, {
          condominium_id: selectedCondominium,
        }));

        if (isMounted) {
          setAvailableCurrencies(response?.data || []);
        }
      } catch (error) {
        if (isMounted) {
          handleErrorMessage(error);
        }
      } finally {
        if (isMounted) {
          setHasLoadedCurrencies(true);
        }
      }
    };

    loadCurrencies();

    return () => {
      isMounted = false;
    };
  }, [selectedCondominium]);

  useEffect(() => {
    register("monedaOriginal");
  }, [register]);

  const currencyCatalog = useMemo(() => {
    const normalized = (availableCurrencies || []).map((item) => ({
      ...item,
      id: String(item.id),
      iso: (item?.iso || "").toUpperCase(),
      isFallbackUsd: false,
    }));

    const hasUsd = normalized.some((item) => item.iso === baseCurrency.iso);
    if (!hasUsd) {
      normalized.push({
        id: baseCurrency.iso,
        iso: baseCurrency.iso,
        name: getCurrencyName(baseCurrency.iso),
        isFallbackUsd: true,
      });
    }

    return normalized;
  }, [availableCurrencies, baseCurrency.iso]);

  const preferredDefaultCurrency = useMemo(() => {
    const fallbackByBase = currencyCatalog.find(
      (item) => (item?.iso || "").toUpperCase() === baseCurrency.iso
    );

    return fallbackByBase || currencyCatalog[0] || null;
  }, [baseCurrency.iso, currencyCatalog]);

  const watchedOriginalCurrency = watch("monedaOriginal") || "";
  const watchedOriginalAmount = watch("montoOriginal");
  const watchedIncomeDate = watch("income_date");

  useEffect(() => {
    if (!hasLoadedCurrencies || hasInitializedCurrencyDefault || !preferredDefaultCurrency) {
      return;
    }

    const defaultId = String(preferredDefaultCurrency.id);
    setSelectedCurrencyId(defaultId);
    setValue("monedaOriginal", defaultId, {
      shouldDirty: false,
      shouldValidate: true,
    });

    setHasInitializedCurrencyDefault(true);
  }, [hasLoadedCurrencies, hasInitializedCurrencyDefault, preferredDefaultCurrency, setValue]);

  const selectedCurrencyData = useMemo(() => {
    const currentValue = String(selectedCurrencyId || watchedOriginalCurrency || "");
    return (currencyCatalog || []).find(
      (item) =>
        String(item.id) === currentValue ||
        item.iso === currentValue.toUpperCase()
    );
  }, [currencyCatalog, selectedCurrencyId, watchedOriginalCurrency]);

  const isSelectedCurrencyUsd =
    (selectedCurrencyData?.iso || "").toUpperCase() === baseCurrency.iso;

  const parsedIncomeDate = useMemo(() => {
    if (!watchedIncomeDate) {
      return null;
    }

    if (moment.isMoment(watchedIncomeDate) && watchedIncomeDate.isValid()) {
      return watchedIncomeDate;
    }

    const parsedDate = moment(watchedIncomeDate, [
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD",
      "DD/MM/YYYY",
    ]);
    return parsedDate.isValid() ? parsedDate : null;
  }, [watchedIncomeDate]);

  const {
    rateByDate,
    rateResolution,
    isRateLoading,
    appliedExchangeRate,
  } = useExchangeRateForDate({
    currencyId: selectedCurrencyData?.id,
    isUsdCurrency: isSelectedCurrencyUsd || Boolean(selectedCurrencyData?.isFallbackUsd),
    date: parsedIncomeDate,
    activeExchangeRate,
    condominiumId: selectedCondominium,
  });

  const isSelectedDateToday =
    Boolean(parsedIncomeDate) && parsedIncomeDate.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");

  const rateHelpText = useMemo(() => {
    if (isSelectedCurrencyUsd) {
      return "";
    }

    if (isRateLoading) {
      return "Consultando tasa...";
    }

    if (!appliedExchangeRate) {
      return "";
    }

    const effectiveDateKey = rateResolution?.usedDateKey || rateResolution?.selectedDateKey;
    const effectiveRateDate = effectiveDateKey
      ? moment(effectiveDateKey)
      : (parsedIncomeDate?.isValid() ? parsedIncomeDate : moment());

    const dateLabel = effectiveRateDate.format("DD/MM/YYYY");
    const rateLabel = formatAmountRounded(appliedExchangeRate, 2);
    return `Tasa ${dateLabel}: ${rateLabel}`;
  }, [appliedExchangeRate, isRateLoading, isSelectedCurrencyUsd, parsedIncomeDate, rateResolution]);

  const calculatedUsdAmount = useMemo(() => {
    return convertToUsd(
      watchedOriginalAmount,
      isSelectedCurrencyUsd,
      appliedExchangeRate
    );
  }, [watchedOriginalAmount, isSelectedCurrencyUsd, appliedExchangeRate]);

  const {
    data: condominiumBanks,
    handler: {
      goToPage: cbGoToPage,
      nextPage: cbNextPage,
      perPage: cbPerPage,
      previousPage: cbPreviousPage,
    },
    isLoading: cbIsLoading,
    meta: cbMeta,
  } = useSWRFetchPagination(fetchCondominiumBanks, params);

  const handleInputChange = (event) => {
    if (event.target.value === "ADVANCES") {
      collapseNoFrecuenteOpen(false);
      setOpenFrecuente(true);
    }
  };

  const handleImageChange = (event) => {
    if (event.target.files.length !== 0) {
      setImagePreview(URL.createObjectURL(event.target.files[0]));
      console.log(event.target.files[0]);
      register("voucher");
      setValue("voucher", event.target.files[0]);
      setFile(event.target.files[0]);
    }
  };

  const create = async (values) => {
    if (!file) {
      setError("voucher", {
        message: "El comprobante de pago es requerido.",
      });
      return;
    }

    if (!isSelectedCurrencyUsd && (!appliedExchangeRate || appliedExchangeRate <= 0 || rateResolution?.isFallback)) {
      Toast.fire({
        type: "error",
        title: "No hay una tasa del dia disponible para convertir esta moneda a USD.",
      });
      return;
    }

    const {
      voucher,
      name,
      description,
      reference,
      bank_name,
      montoOriginal,
      bank_id,
      is_common_type,
      reserve_fund_id,
      destination,
      income_date,
    } = values;

    const normalizedUsdAmount = convertToUsd(
      montoOriginal,
      isSelectedCurrencyUsd,
      appliedExchangeRate
    );

    try {
      let formData = new FormData();
      formData.append("name", name);
      formData.append("description", description);
      formData.append("bank_name", bank_name);
      formData.append("bank_id", bank_id);
      /* formData.append("reserve_fund_id", reserve_fund_id); */
      formData.append("reference", reference);
      formData.append("condominium_id", id);
      formData.append("amount", normalizedUsdAmount);
      // HO-TASK-253: la fecha del ingreso se guarda, y la tasa que viaja es la
      // de esa fecha (la misma con la que se convirtió el monto). Cargado en
      // dólares va vacía y el backend la completa con la registrada ese día.
      if (income_date) formData.append("income_date", moment(income_date).format("YYYY-MM-DD"));
      const rateToStore = isSelectedCurrencyUsd ? null : appliedExchangeRate;
      if (rateToStore) formData.append("rate", rateToStore);
      formData.append("is_common_type", 1);
      formData.append("destination", destination);

      if (destination === "RESERVE_FUND")
        formData.append("reserve_fund_id", reserve_fund_id);
      if (values.accounting_area_id) formData.append("accounting_area_id", values.accounting_area_id);

      if (file) formData.append("voucher", file);

      const { code } = await createIncome(formData);

      console.log(code);

      if (code === SUCCESS_RESPONSE.CREATED) {
        setRedirectUrl(`/dashboard/${moduleBase}/${id}/common-income`);
        setRedirect(true);
        Toast.fire({
          type: "success",
          title: `Ingreso registrado exitosamente.`,
        });
      }
    } catch (error) {
      console.log(error);
      handleErrorMessage(error);
    }
  };

  const [bank, setBank] = useState(null);
  const [selectedBankId, setSelectedBankId] = useState(null);

  useEffect(() => {
    if (selectedBankId) {
      clearErrors("bank_id");
    }
  }, [selectedBankId]);

  const [funds, setFunds] = useState([]);
  const fetchApi = async () => {
    const response = await fetchCondominiumBalance(selectedCondominium);
    setFunds(response.data && response.data.length >= 0 ? response.data : []);
  };
  useEffect(() => {
    fetchApi();
  }, []);

  const [selectFunds, setSelectFunds] = useState([]);
  const fetchApiFunds = async () => {
    const response = await fetchCondominiumFunds(selectedCondominium);
    setSelectFunds(
      response.data && response.data.length >= 0 ? response.data : []
    );
    console.log("fondos", response.data);
  };
  useEffect(() => {
    fetchApiFunds();
  }, []);

  // Árbol completo del plan de cuentas, usado solo para inferir la cuenta
  // contable por defecto (código "4" = INGRESOS) al crear una nueva área.
  const [accountPlansTree, setAccountPlansTree] = useState([]);
  const fetchApiAccountPlans = async () => {
    try {
      const response = await fetchAccountPlans(selectedCondominium);
      setAccountPlansTree(response?.data || []);
    } catch (error) {
      console.error("Error fetching account plans:", error);
    }
  };

  useEffect(() => {
    fetchApiAccountPlans();
  }, []);

  const findAccountPlanByCode = (plans, code) => {
    for (const plan of plans) {
      if (plan.code === code) return plan;
      if (plan.children?.length) {
        const found = findAccountPlanByCode(plan.children, code);
        if (found) return found;
      }
    }
    return null;
  };

  const [accountingAreas, setAccountingAreas] = useState([]);
  const fetchApiAccountingAreas = async () => {
    try {
      const response = await fetchAccountingAreas(selectedCondominium);
      setAccountingAreas(response.data || []);
    } catch (error) {
      console.error("Error fetching accounting areas:", error);
    }
  };

  useEffect(() => {
    fetchApiAccountingAreas();
  }, []);

  // El endpoint trae TODAS las áreas contables del condominio (de gastos y de
  // ingresos mezcladas); acá solo dejamos pasar las que apuntan a una cuenta
  // de Ingresos (grupo "4") o Ingresos Varios (grupo "7"), para no poder
  // registrar un ingreso contra una cuenta de gasto por error.
  const incomeAccountingAreas = accountingAreas.filter((area) => {
    const code = area.account_plan?.code || area.accountPlan?.code;
    return code && (code.startsWith("4") || code.startsWith("7"));
  });

  const [createAccountAreaModalOpen, setCreateAccountAreaModalOpen] = useState(false);
  const [newAreaName, setNewAreaName] = useState("");
  const [isCreatingArea, setIsCreatingArea] = useState(false);
  const [pendingAreaId, setPendingAreaId] = useState(null);

  useEffect(() => {
    if (pendingAreaId && incomeAccountingAreas.some((area) => area.id === pendingAreaId)) {
      setValue("accounting_area_id", pendingAreaId, { shouldValidate: true });
      setPendingAreaId(null);
    }
  }, [accountingAreas, pendingAreaId, setValue]);

  const handleCreateAccountingArea = async () => {
    // Sin este guard, varios clicks rápidos disparan varios POST antes de que
    // el primero responda, creando la misma área duplicada.
    if (!newAreaName || isCreatingArea) return;
    setIsCreatingArea(true);
    try {
      const defaultPlan = findAccountPlanByCode(accountPlansTree, "7.1");
      const response = await createAccountingArea({
        condominium_id: id,
        name: newAreaName,
        account_plan_id: defaultPlan ? defaultPlan.id : null,
      });
      if (response.code === SUCCESS_RESPONSE.OK || response.code === SUCCESS_RESPONSE.CREATED) {
        Toast.fire({
          type: "success",
          title: "Área creada con éxito",
        });
        setNewAreaName("");
        setCreateAccountAreaModalOpen(false);
        setPendingAreaId(response.data?.id ?? null);
        fetchApiAccountingAreas();
      }
    } catch (error) {
      handleErrorMessage(error);
    } finally {
      setIsCreatingArea(false);
    }
  };

  const destination = watch("destination") || "CASH";

  // Si solo hay un fondo de reserva disponible, se selecciona automáticamente
  // en vez de obligar a elegirlo a mano (mismo criterio que la moneda del
  // condominio en condominiumActions.jsx).
  useEffect(() => {
    if (
      destination === "RESERVE_FUND" &&
      selectFunds.length === 1 &&
      !watch("reserve_fund_id")
    ) {
      setValue("reserve_fund_id", `${selectFunds[0].id}`);
    }
    // eslint-disable-next-line
  }, [destination, selectFunds]);

  console.log(condominiumBanks);
  const renderBanksItems = () => {
    return condominiumBanks.map((bank, i) => {
      return (
        <SelectBankListItem
          key={i}
          // `condominium_banks_id` (la cuenta de este banco EN este
          // condominio), no `bank.bank_id` (el banco genérico, ej: "BBVA"
          // como marca, compartido entre condominios) -- income.bank_id
          // referencia condominium_banks.id, así que mandar bank.bank_id
          // registraba el ingreso contra el banco de OTRO condominio, que es
          // el que quedaba en esa fila. Mismo criterio que PropertyToPays.
          id={bank.condominium_banks_id}
          selected={selectedBankId}
          onSelect={() => {
            setBank(bank);
            setSelectedBankId(bank.condominium_banks_id);
            setValue("bank_id", bank.condominium_banks_id);
          }}
          title={bank?.name}
          alternativeName="banco"
          createdAt={bank.created_at}
          owner_dni={bank.owner_dni}
          owner_name={bank.owner_name}
          account={bank.account_number}
        />
      );
    });
  };

  // const [selectBanks, setSelectBanks] = useState([]);
  // const fetchApiBaks = async () => {
  //   const responseBank = await fetchCondominiumBanks(selectedCondominium);
  //   setSelectBanks(
  //     responseBank.data && responseBank.data.length >= 0
  //       ? responseBank.data
  //       : []
  //   );
  // };
  // useEffect(() => {
  //   fetchApiBaks();
  // }, []);

  // const renderSelectBankItems = () => {
  //   return selectBanks > 0
  //     ? selectBanks.map((selectBank) => (
  //       <option
  //         defaultValue="1"
  //         value={selectBank.name}
  //         key={selectBank.name}
  //       >
  //         {selectBank.name}
  //       </option>
  //     ))
  //     : "No hay bancos registrados";
  // };
  const onSubmit = (data) => {
    console.log(data);
    setDataCredit({
      ...dataCredit,
      data,
    });
  };

  useEffect(() => {
    if (imagePreview) {
      clearErrors("voucher");
    }
    // eslint-disable-next-line
  }, [imagePreview]);

  const renderForm = () => (
    <>
      <FalconCardHeader title="Detalle del ingreso común" light={false} />
      <CardBody>
        <Row form>
          <Col sm>
            <FormGroup>
              <Label for="name">Nombre</Label>
              <Input
                id="name"
                name="name"
                innerRef={register({
                  required: "El nombre del ingreso es requerido.",
                  minLength: {
                    value: 4,
                    message:
                      "El nombre del ingreso debe tener al menos 4 caracteres.",
                  },
                  /*      pattern: {
                    value: /^[a-zA-Z0-9 %]+$/i,
                    message: "Solo se aceptan alfanúmericos.",
                  }, */
                })}
                autoComplete="off"
                placeholder="Nombre del ingreso"
                className={classNames({
                  "border-danger": errors["name"]?.message,
                })}
              />
              {errors["name"] && (
                <WizardError
                  error={errors["name"].message}
                  className="fs--1 font-weight-normal d-block"
                />
              )}
            </FormGroup>
          </Col>
          <Col sm tag={FormGroup}>
            <Label for="destination">Destino de los fondos</Label>
            <Controller
              name="destination"
              control={control}
              defaultValue="CASH"
              rules={{ required: "Selecciona el destino de los fondos" }}
              render={({ onChange, value }) => (
                <Input
                  type="select"
                  name="destination"
                  value={value}
                  onChange={(event) => {
                    setValue("destination", event.target.value);
                    onChange(event.target.value);
                  }}
                  className={classNames({
                    "border-danger": errors["destination"]?.message,
                  })}
                >
                  <option value="CASH">Caja</option>
                  <option value="PLANILLA">
                    Planilla de liquidación de gastos
                  </option>
                  <option value="RESERVE_FUND">Fondo de reserva</option>
                </Input>
              )}
            />
            <WizardError
              error={errors["destination"]?.message}
              className="fs--1 font-weight-normal d-block"
            />
          </Col>
          {destination === "RESERVE_FUND" && (
            <Col sm tag={FormGroup}>
              <Label for="reserve_fund_id">Fondo de reserva</Label>
              <Controller
                name="reserve_fund_id"
                control={control}
                defaultValue=""
                rules={{
                  required: {
                    value: true,
                    message: "Seleccione un fondo de reserva",
                  },
                }}
                render={({ onChange }) => (
                  <Input
                    type="select"
                    name="reserve_fund_id"
                    // El valor se lee de watch() en vez del "value" que entrega
                    // Controller: éste último no se resincroniza cuando el auto-select
                    // (más abajo) cambia el campo vía setValue() en lugar de onChange,
                    // dejando el <select> visualmente marcado pero el form en blanco.
                    value={watch("reserve_fund_id") || ""}
                    onChange={(event) => {
                      setValue("reserve_fund_id", event.target.value);
                      onChange(event.target.value);
                    }}
                    className={classNames({
                      "border-danger": errors["reserve_fund_id"]?.message,
                    })}
                  >
                    {selectFunds.length > 0 ? (
                      <>
                        <option value="" disabled>
                          Selecciona un fondo...
                        </option>
                        {selectFunds.map((fund) => (
                          <option key={fund.id} value={fund.id}>
                            {fund.name}
                          </option>
                        ))}
                      </>
                    ) : (
                      <option disabled value="">
                        No hay fondos
                      </option>
                    )}
                  </Input>
                )}
              />
              <WizardError
                error={errors["reserve_fund_id"]?.message}
                className="fs--1 font-weight-normal d-block"
              />
            </Col>
          )}
        </Row>

        <Row form>
          <Col sm>
            <FormGroup>
              <Label for="description">Descripción</Label>
              <Input
                autoComplete="off"
                name="description"
                innerRef={register({
                  minLength: {
                    value: 4,
                    message:
                      "La descripción debe tener al menos 4 carácteres.",
                  },
                })}
                type={"textarea"}
                className={`${errors["description"]?.message ? "border-danger" : ""
                  } form-control`}
                placeholder="Descripción del gasto"
              />
              <WizardError
                error={errors["description"]?.message}
                className="fs--1 font-weight-normal d-block"
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col tag={FormGroup}>
            <Label>Seleccione un banco del condominio</Label>
            <Row>
              <Col tag={FormGroup} xs={9} className="pr-0">
                <Input
                  type="text"
                  placeholder="Ingrese el nombre de un banco"
                  onChange={(event) => setSearch(event.target.value)}
                />
              </Col>
              <Col tag={FormGroup} xs={3}>
                <ButtonIcon
                  className="btn-block"
                  style={{ whiteSpace: "nowrap" }}
                  icon={faSearch}
                  color="falcon-default"
                  onClick={() =>
                    setParams((prev) => {
                      return { ...prev, search: search };
                    })
                  }
                >
                  Buscar
                </ButtonIcon>
              </Col>
            </Row>
            <Row>
              <Col>
                {!cbIsLoading ? (
                  condominiumBanks.length >= 1 ? (
                    renderBanksItems()
                  ) : (
                    <p>No se han encontrado bancos.</p>
                  )
                ) : (
                  <Loader padding="py-3" />
                )}
                <FullPagination
                  nextPage={cbNextPage}
                  prevPage={cbPreviousPage}
                  goToPage={cbGoToPage}
                  meta={cbMeta}
                  className={"shadow-none"}
                  posText={"bancos"}
                  perPage={cbPerPage}
                />
                {!selectedBankId && (
                  <span
                    className={classNames(
                      "text-danger d-inline-block fs--1 font-weight-normal d-block"
                    )}
                  >
                    Debes seleccionar un banco
                  </span>
                )}
                <WizardError
                  error={errors["bank_id"]?.message}
                  className="fs--1 font-weight-normal d-block"
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <FormGroup>
          <Label for="name">Nombre del banco</Label>
          <Input
            disabled
            defaultValue={bank?.name || undefined}
            autoComplete="off"
            name="name"
            id="title"
            placeholder="Nombre del banco"
          />
        </FormGroup>
        <Row form>
          <Col sm tag={FormGroup}>
            <Label for="account_number">Número de cuenta</Label>
            <NumberFormat
              disabled
              value={bank?.account_number || undefined}
              format="#### #### #### #### #### #### ####"
              placeholder="Número de cuenta"
              className="form-control"
              renderText={(value, props) => <div {...props}>{value}</div>}
            />
          </Col>
          <Col sm tag={FormGroup}>
            <Label for="owner_name">Nombre del propietario</Label>
            <Input
              disabled
              defaultValue={bank?.owner_name || undefined}
              autoComplete="off"
              name="owner_name"
              id="owner_name"
              placeholder="Nombre del propietario"
            />
          </Col>
        </Row>
        <Row form>
          <Col sm tag={FormGroup}>
            <Label for="owner_dni">Identificación (DNI)</Label>
            <Input
              defaultValue={bank?.owner_dni || undefined}
              id="owner_dni"
              name="owner_dni"
              disabled
              type="text"
              placeholder="Identificación"
            />
          </Col>
        </Row>

        <Row form>
          <Col sm tag={FormGroup}>
            <Label for="accounting_area_id">Cuenta / Área Contable</Label>
            <Input
              type="select"
              name="accounting_area_id"
              innerRef={register({
                required: "Debes seleccionar una cuenta / área contable.",
              })}
              className={classNames({
                "border-danger": errors["accounting_area_id"]?.message,
              })}
              defaultValue=""
            >
              <option value="" disabled>
                Selecciona una Cuenta / Área
              </option>
              {incomeAccountingAreas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </Input>
            {errors["accounting_area_id"] && (
              <WizardError
                error={errors["accounting_area_id"].message}
                className="fs--1 font-weight-normal d-block"
              />
            )}
            <Button
              color="link"
              size="sm"
              className="p-0 mt-1"
              onClick={() => setCreateAccountAreaModalOpen(true)}
            >
              + Nueva Área/Categoría
            </Button>
          </Col>
        </Row>
        {/* <Collapse isOpen={openFrecuente}>
          <Row form>
            <Col sm={12} tag={FormGroup}>
              {selectFunds > 0 ? (
                <Input
                  innerRef={register({
                    required: { value: true, message: "Ingresar el monto" },
                  })}
                  type="select"
                  name="fund"
                  id="fund"
                  onChange={handleInputChange}
                  onClick={() => {
                    collapseNoFrecuenteOpen(false);
                    setOpenFrecuente(true);
                  }}
                >
                  <option selected value="Selecciona">
                    Seleccione fondo de reserva
                  </option>
                  {renderSelectFundsItems()}
                </Input>
              ) : (
                renderSelectFundsItems()
              )}
            </Col>
          </Row>
        </Collapse>  */}
        <Row form>
          <Col sm>
            <FormGroup>
              <Label for="reference">Numero de referencia</Label>
              <Input
                id="reference"
                name="reference"
                type="number"
                autoComplete="off"
                min={0}
                placeholder="Referencia"
                className={classNames({
                  "border-danger": errors?.reference?.message,
                })}
                innerRef={register({
                  required: "El número de referencia es obligatorio.",
                  minLength: {
                    value: 5,
                    message:
                      "El número de referencia debe tener al menos 5 caracteres.",
                  },
                })}
              />
              <WizardError
                error={errors?.reference?.message}
                className="fs--1 font-weight-normal d-block"
              />
            </FormGroup>
          </Col>
          <Col sm tag={FormGroup}>
            <Label for="income_date">Fecha del ingreso</Label>
            <Datetime
              className={errors.income_date?.message ? "border-danger" : ""}
              name="income_date"
              innerRef={register("income_date", {
                required: "La fecha del ingreso es obligatoria.",
              })}
              value={watch("income_date")}
              onChange={(d) => {
                let formattedDate = "";
                if (moment.isMoment(d) && d.isValid()) {
                  formattedDate = d.format("YYYY-MM-DD HH:mm:ss");
                } else if (typeof d === "string") {
                  const parsed = moment(d, ["DD/MM/YYYY", "YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss"]);
                  if (parsed.isValid()) {
                    formattedDate = parsed.format("YYYY-MM-DD HH:mm:ss");
                  } else {
                    formattedDate = d;
                  }
                } else if (d) {
                  const parsed = moment(d);
                  if (parsed.isValid()) {
                    formattedDate = parsed.format("YYYY-MM-DD HH:mm:ss");
                  }
                }
                setValue("income_date", formattedDate, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              dateFormat="DD/MM/YYYY"
              timeFormat={false}
            />
            <WizardError
              error={errors.income_date?.message}
              className="fs--1 font-weight-normal d-block"
            />
          </Col>
        </Row>

        <Row form>
          <Col sm tag={FormGroup}>
            <Label for="acronym">Monto Total</Label>

            <Controller
              name="montoOriginal"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: "Ingresar el monto",
                },
              }}
              render={({ onChange, value }) => (
                <>
                  <InputWithLeftSelect
                    selectValue={String(selectedCurrencyId || watchedOriginalCurrency || selectedCurrencyData?.id || "")}
                    onSelectChange={(event) => {
                      const nextVal = String(event.target.value);
                      setSelectedCurrencyId(nextVal);
                      setValue("monedaOriginal", nextVal, {
                        shouldDirty: true,
                        shouldValidate: true,
                        shouldTouch: true,
                      });
                    }}
                    selectOptions={(currencyCatalog || []).map((item) => ({
                      value: String(item.id),
                      label: `${item.iso} (${item.name})`,
                    }))}
                    errorMessage={errors.montoOriginal?.message}
                    helpText={rateHelpText}
                    selectAriaLabel="Moneda original"
                    selectMinWidth="120px"
                  >
                    <NumberFormat
                      customInput={Input}
                      thousandSeparator
                      prefix={
                        selectedCurrencyData?.iso
                          ? `${selectedCurrencyData.iso} `
                          : `${baseCurrency.symbol} `
                      }
                      fixedDecimalScale
                      decimalScale={2}
                      onValueChange={(vals) => onChange(vals.value)}
                      value={value}
                      placeholder="0.00"
                      className={classNames("font-weight-bold border-left-0", {
                        "border-danger": errors.montoOriginal,
                      })}
                    />
                  </InputWithLeftSelect>
                  {isRateLoading && (
                    <small className="text-muted d-block mt-1">
                      <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                      Consultando tasa para la fecha...
                    </small>
                  )}
                </>
              )}
            />

            <WizardError
              error={errors.montoOriginal?.message}
              className="fs--1 font-weight-normal d-block"
            />
          </Col>
        </Row>

        {Boolean(watchedOriginalAmount) && !isSelectedCurrencyUsd && (
          <Card className="border-warning bg-soft-warning mb-3">
            <CardBody className="py-3">
              <div className="d-flex align-items-start">
                <div>
                  {Boolean(calculatedUsdAmount) && (
                    <div className="text-700 fs--1 font-weight-semi-bold mb-1">
                      Monto equivalente a registrar: ${" "}
                      <NumberFormat
                        displayType="text"
                        value={formatAmountRounded(calculatedUsdAmount, 2)}
                        thousandSeparator
                        decimalScale={2}
                        fixedDecimalScale
                      />{" "}
                      USD
                    </div>
                  )}
                  <div className="text-700 fs--1">
                    El monto que se guardará en el sistema será el equivalente en {getCurrencyName(baseCurrency.iso)} {baseCurrency.symbol}.
                    El valor mostrado arriba es solo la referencia original ingresada por el usuario.
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        <Row form>
          <Col sm tag={FormGroup}>
            <FileUploadArea
              onChange={handleImageChange}
              preview={imagePreview}
              fileName={file?.name}
              onRemove={() => { setFile(null); setImagePreview(null); }}
              error={errors["voucher"]?.message}
            />
          </Col>
        </Row>

        <Row>
          <Col className="d-flex justify-content-end">
            <ButtonIcon
              color="falcon-default"
              className="ml-auto px-5 mr-2"
              icon={faUndo}
              disabled={isSubmitting}
              onClick={
                () =>
                  history.push({
                    pathname: `/dashboard/${moduleBase}/${id}/common-income`,
                  })

                /*       setRedirectUrl(`/dashboard/${moduleBase}/${id}/common-costs`);
                setRedirect(true); */
              }
              iconAlign="right"
              transform="down-1 shrink-4"
            >
              Cancelar
            </ButtonIcon>
            <ButtonIcon
              data-testid="submit-button"
              color="primary"
              className="px-5 ml-2"
              type="submit"
              icon={isSubmitting ? faSpinner : "save"}
              iconClassName={isSubmitting ? "fa-spin" : ""}
              iconAlign="right"
              disabled={isSubmitting || isRateLoading}
              transform="down-1 shrink-4"
            >
              {isSubmitting ? "Guardando..." : "Guardar"}
            </ButtonIcon>
          </Col>
        </Row>

        <Modal
          isOpen={createAccountAreaModalOpen}
          toggle={() => setCreateAccountAreaModalOpen(!createAccountAreaModalOpen)}
        >
          <div className="modal-header">
            <h5 className="modal-title">Crear Nueva Área / Categoría de Ingreso</h5>
            <button
              type="button"
              className="close"
              onClick={() => setCreateAccountAreaModalOpen(false)}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <ModalBody>
            <FormGroup>
              <Label for="newAreaName">Nombre del Área (ej: Cuotas de mantenimiento)</Label>
              <Input
                type="text"
                id="newAreaName"
                value={newAreaName}
                onChange={(e) => setNewAreaName(e.target.value)}
                placeholder="Ingrese el nombre"
              />
            </FormGroup>
            <div className="d-flex justify-content-end">
              <Button
                color="secondary"
                className="mr-2"
                onClick={() => setCreateAccountAreaModalOpen(false)}
                disabled={isCreatingArea}
              >
                Cancelar
              </Button>
              <Button color="primary" onClick={handleCreateAccountingArea} disabled={isCreatingArea}>
                {isCreatingArea ? "Creando..." : "Crear Área"}
              </Button>
            </div>
          </ModalBody>
        </Modal>
      </CardBody>
    </>
  );

  return (
    <Card
      data-testid="form"
      tag={Form}
      onSubmit={handleSubmit(create)}
      className="bg-light"
    >
      <PreloadState
        loading={isSubmitting}
        message="Guardando ingreso común..."
        overlay
      >
        {renderForm()}
      </PreloadState>
    </Card>
  );
};

export default CreditPaymentForm;
